import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationProvider, routeForNotification, useNotifications } from "@/context/NotificationContext";
import { setFaviconBadge } from "@/lib/favicon";
import { Toaster, toast } from "@/components/ui/sonner";
import { MobileShell } from "@/components/MobileShell";
import { BottomNav as VCBottomNav } from "@/components/vc/BottomNav";
import { EmployeeBottomNav } from "@/components/EmployeeBottomNav";
import { NotificationPanel } from "@/components/vc/NotificationPanel";

import Login from "@/pages/Login";
import AcceptInvite from "@/pages/AcceptInvite";

import VCDashboard from "@/pages/vc/Dashboard";
import VCBusinesses from "@/pages/vc/Businesses";
import VCEnquiries from "@/pages/vc/Enquiries";
import VCRaiseEnquiry from "@/pages/vc/RaiseEnquiry";
import VCReviewEnquiry from "@/pages/vc/ReviewEnquiry";
import VCSupplierQuotation, { VCIncomingEnquiries } from "@/pages/vc/SupplierQuotation";
import VCEnquiryDetail from "@/pages/vc/EnquiryDetail";
import VCAddEnquiryItems from "@/pages/vc/AddEnquiryItems";
import VCOrdersDashboard from "@/pages/vc/OrdersDashboard";
import VCGenerateOrder from "@/pages/vc/GenerateOrder";
import VCOrderReview from "@/pages/vc/OrderReview";
import VCAssignOrder from "@/pages/vc/AssignOrder";
import VCOrderDetail from "@/pages/vc/OrderDetail";
import VCIncomingOrderDetail from "@/pages/vc/IncomingOrderDetail";
import VCEmployees from "@/pages/vc/Employees";
import VCEmployeeDetail from "@/pages/vc/EmployeeDetail";
import VCDeletedEmployees from "@/pages/vc/DeletedEmployees";
import VCConnectInvite from "@/pages/vc/ConnectInvite";
import VCAssignTask from "@/pages/vc/AssignTask";
import VCQRLibrary from "@/pages/vc/QRLibrary";
import VCBusinessPortfolio from "@/pages/vc/BusinessPortfolio";
import OrderChat from "@/pages/vc/OrderChat";
import Customers from "@/pages/Customers";
import CustomerForm from "@/pages/CustomerForm";
import Suppliers from "@/pages/Suppliers";
import SupplierForm from "@/pages/SupplierForm";
import NewOrder from "@/pages/NewOrder";
import Tasks from "@/pages/Tasks";
import TaskForm from "@/pages/TaskForm";
import Employees from "@/pages/Employees";
import BulkImport from "@/pages/BulkImport";
import BulkComparison from "@/pages/BulkComparison";
import InboundOrders from "@/pages/InboundOrders";
import Settings from "@/pages/Settings";

import EmployeeDashboard from "@/pages/EmployeeDashboard";
import EmployeeTasks from "@/pages/EmployeeTasks";
import EmployeeTaskDetail from "@/pages/EmployeeTaskDetail";
import EmployeeProfile from "@/pages/EmployeeProfile";

function Loading() {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-4 border-emerald-200 border-t-emerald-600 animate-spin" />
        <p className="text-sm text-slate-500 font-medium">Loading KonexoCRM...</p>
      </div>
    </div>
  );
}

function useNotifyOnNew() {
  const nav = useNavigate();
  const audioRef = React.useRef(null);
  React.useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      if (Notification.permission === "default") {
        try { Notification.requestPermission().catch(() => {}); } catch (_e) { /* ignore */ }
      }
    }
    audioRef.current = new Audio(
      "data:audio/wav;base64,UklGRlIEAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YS4EAAAAAAB/AP7/gv/9/4H/AACC//7/gP8AAIH//v+B/wAAAAAAAAAA////AAD/AP//AQAAAP7/AAABAAAA//8AAP///v8AAAEAAgAAAP7/AAABAAAA//8AAAAAAAAAAP//AQAA/wAAAAAAAAEA/v8AAAAAAAABAAEAAAAAAAAA//////8AAAAA/////wAAAAD//wEAAQAAAAAAAAAAAAAA////AAAAAAAAAAABAP//AAAAAP////8BAAAA//8AAAEAAQAAAAAA//8AAAAAAQAAAP7/AAAAAAAAAAAAAAAAAAAAAAAAAAABAAAA//8AAAEAAAAAAAAA////AAABAAAAAAAAAP7/AAABAAEA//8AAAAAAAAAAP//AAABAAEA//8AAAAAAAABAAAA//8AAAAAAQAAAAAAAAAAAAAAAAABAP////8AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA"
    );
  }, []);
  return React.useCallback((newRows, markRead) => {
    const bursts = newRows.slice(0, 3);
    bursts.forEach((n) => {
      toast(n.title, {
        description: n.body || undefined,
        duration: 5000,
        // #3 — tapping "Open" from the popup must mark it read immediately,
        // not just navigate (bell badge should drop right away).
        action: { label: "Open", onClick: () => {
          if (markRead) { try { markRead(n.id); } catch (_e) { /* ignore */ } }
          nav(routeForNotification(n));
        } },
      });
    });
    try { audioRef.current && audioRef.current.play().catch(() => {}); } catch (_e) { /* ignore */ }
    try { navigator.vibrate && navigator.vibrate([80, 40, 80]); } catch (_e) { /* ignore */ }
    if (typeof document !== "undefined" && document.visibilityState === "hidden"
        && typeof window !== "undefined" && "Notification" in window
        && Notification.permission === "granted") {
      bursts.forEach((n) => {
        try {
          const notif = new Notification(n.title, { body: n.body || "", tag: n.id });
          notif.onclick = () => {
            try { window.focus(); } catch (_e) { /* ignore */ }
            if (markRead) { try { markRead(n.id); } catch (_e) { /* ignore */ } }
            nav(routeForNotification(n));
            notif.close();
          };
        } catch (_e) { /* browser may reject in some contexts */ }
      });
    }
  }, [nav]);
}

function OwnerRoutes() {
  const onNew = useNotifyOnNew();

  return (
    <NotificationProvider enabled onNew={onNew}>
      <FaviconBadgeSync />
      <Routes>
        <Route path="/" element={<VCDashboard />} />
        <Route path="/businesses" element={<VCBusinesses />} />
        <Route path="/enquiries" element={<VCEnquiries />} />

        <Route path="/customers" element={<Customers />} />
        <Route path="/customers/new" element={<CustomerForm />} />
        <Route path="/customers/:id" element={<CustomerForm />} />
        <Route path="/add" element={<CustomerForm />} />
        <Route path="/edit/:id" element={<CustomerForm />} />

        <Route path="/suppliers" element={<Suppliers />} />
        <Route path="/suppliers/new" element={<SupplierForm />} />
        <Route path="/suppliers/:id" element={<SupplierForm />} />

        <Route path="/orders" element={<VCOrdersDashboard />} />
        <Route path="/orders/generate" element={<VCGenerateOrder />} />
        <Route path="/orders/review" element={<VCOrderReview />} />
        <Route path="/orders/assign" element={<VCAssignOrder />} />
        <Route path="/orders/new" element={<NewOrder />} />
        <Route path="/orders/import" element={<BulkImport />} />
        <Route path="/bulk-compare/:id" element={<BulkComparison />} />
        <Route path="/orders/:id" element={<VCOrderDetail />} />
        <Route path="/inbound-orders" element={<InboundOrders />} />
        <Route path="/inbound-orders/:id" element={<VCIncomingOrderDetail />} />
        <Route path="/settings" element={<Settings />} />

        <Route path="/tasks" element={<Tasks />} />
        <Route path="/tasks/new" element={<TaskForm />} />
        <Route path="/tasks/assign" element={<VCAssignTask />} />
        <Route path="/employees" element={<VCEmployees />} />
        <Route path="/employees/legacy" element={<Employees />} />
        <Route path="/employees/deleted" element={<VCDeletedEmployees />} />
        <Route path="/employees/:id" element={<VCEmployeeDetail />} />

        {/* Redesigned enquiry flows */}
        <Route path="/enquiries/new" element={<VCRaiseEnquiry />} />
        <Route path="/enquiries/review" element={<VCReviewEnquiry />} />
        <Route path="/enquiries/incoming" element={<VCIncomingEnquiries />} />
        <Route path="/enquiries/incoming/:id" element={<VCSupplierQuotation />} />
        <Route path="/enquiries/:id/add-items" element={<VCAddEnquiryItems />} />
        <Route path="/enquiries/:id" element={<VCEnquiryDetail />} />

        <Route path="/qr" element={<VCQRLibrary />} />
        <Route path="/portfolio" element={<VCBusinessPortfolio />} />
        <Route path="/portfolio/:ownerId" element={<VCBusinessPortfolio />} />
        <Route path="/chat/:orderId" element={<OrderChat />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <VCBottomNav />
      <NotificationPanel />
    </NotificationProvider>
  );
}

// Small helpers below OwnerRoutes so they live next to their consumer.
function FaviconBadgeSync() {
  const { counts } = useNotifications();
  React.useEffect(() => {
    setFaviconBadge(counts?.total || 0);
  }, [counts?.total]);
  return null;
}

function EmployeeRoutes() {
  const onNew = useNotifyOnNew();

  return (
    <NotificationProvider enabled onNew={onNew}>
      <FaviconBadgeSync />
      <Routes>
        <Route path="/" element={<EmployeeDashboard />} />
        <Route path="/my-tasks" element={<EmployeeTasks />} />
        <Route path="/my-tasks/:id" element={<EmployeeTaskDetail />} />
        <Route path="/profile" element={<EmployeeProfile />} />
        <Route path="/chat/:orderId" element={<OrderChat />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <EmployeeBottomNav />
      <NotificationPanel />
    </NotificationProvider>
  );
}

function PublicRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/employee/accept" element={<AcceptInvite />} />
      <Route path="/welcome" element={<LandingRedirect />} />
      <Route path="/" element={<LandingRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Renders the landing page HTML from /public/landing.html as a full-viewport
// iframe. This keeps the marketing site pixel-identical to the artifact upload
// and lets its embedded "Get Started" anchor drop the visitor onto /login.
function LandingRedirect() {
  return (
    <div style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", background: "#0b1220" }}>
      <iframe
        title="KonexoCRM"
        src="/landing.html"
        data-testid="landing-iframe"
        style={{ width: "100%", height: "100%", border: 0 }}
      />
    </div>
  );
}

function Shell() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  // Allow accept-invite route to be reachable regardless of auth state
  if (typeof window !== "undefined" && window.location.pathname === "/employee/accept") {
    return (
      <MobileShell>
        <Routes>
          <Route path="/employee/accept" element={<AcceptInvite />} />
        </Routes>
      </MobileShell>
    );
  }
  // Phase 7 #2 — business connection deep link must open directly to the
  // invite screen whether the recipient is logged in, logged out, or brand
  // new; ConnectInvite.jsx branches internally on auth state.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/connect/")) {
    return (
      <MobileShell>
        <Routes>
          <Route path="/connect/:code" element={<VCConnectInvite />} />
        </Routes>
      </MobileShell>
    );
  }
  // #2 — Unauthenticated users must NOT see the authenticated app chrome
  // (DesktopSidebar / MobileShell). Landing and login are rendered raw.
  if (!user) {
    return <PublicRoutes />;
  }
  return (
    <MobileShell showSidebar={user.role !== "employee"}>
      {user.role === "employee" ? <EmployeeRoutes /> : <OwnerRoutes />}
    </MobileShell>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
